import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { usePublicCopy } from "@/i18n/karatePublicI18n.js";
import * as r0 from "@/api/karateR0InstructorsApi.js";

function shortBio(text, max = 120) {
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max).trim()}…`;
}

function InstructorPhoto({ instructor }) {
  if (!instructor.photoUrl) {
    return (
      <div className="instructor-card__photo instructor-card__photo--placeholder" aria-hidden="true">
        {instructor.firstName?.[0]}
        {instructor.lastName?.[0]}
      </div>
    );
  }
  return (
    <img
      className="instructor-card__photo"
      src={instructor.photoUrl}
      alt={`${instructor.firstName} ${instructor.lastName}`}
      loading="lazy"
    />
  );
}

export function PublicInstructorsR0Page() {
  const { copy } = usePublicCopy();
  const d = copy.directory;
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const loadInstructors = useCallback(async () => {
    setLoading(true);
    const result = await r0.fetchPublicInstructors();
    setLoading(false);
    if (!result.ok) {
      setError(result.body?.error ?? d.emptyInstructors ?? "Impossible de charger les instructeurs.");
      return;
    }
    setInstructors(result.body.instructors ?? []);
  }, [d.emptyInstructors]);

  useEffect(() => {
    loadInstructors();
  }, [loadInstructors]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return instructors;
    return instructors.filter(
      (i) =>
        i.firstName.toLowerCase().includes(term) ||
        i.lastName.toLowerCase().includes(term) ||
        (i.title ?? "").toLowerCase().includes(term) ||
        (i.dojo?.name ?? "").toLowerCase().includes(term) ||
        (i.dojo?.city ?? "").toLowerCase().includes(term),
    );
  }, [instructors, search]);

  return (
    <PageSection title={d.instructorsTitle} subtitle={d.instructorsSubtitle} testId="page-instructeurs">
      <div className="accounts-toolbar" data-testid="instructors-public-toolbar">
        <input
          type="search"
          placeholder={d.searchInstructors ?? "Rechercher…"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="instructors-public-search"
          aria-label={d.searchInstructors ?? "Rechercher un instructeur"}
        />
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => setSearch("")}
          data-testid="instructors-public-reset"
        >
          {copy.directory?.resetFilters ?? "Réinitialiser"}
        </button>
      </div>

      {error ? (
        <p className="form-error" role="alert" data-testid="instructors-public-error">
          {error}
        </p>
      ) : null}

      {loading ? <p data-testid="instructors-public-loading">{copy.directory?.loading ?? "Chargement…"}</p> : null}

      {!loading && filtered.length === 0 ? (
        <p className="accounts-empty" data-testid="instructors-public-empty">
          {d.emptyInstructors ?? "Aucun instructeur trouvé."}
        </p>
      ) : null}

      {!loading && filtered.length > 0 ? (
        <div className="instructors-grid" data-testid="instructors-public-grid">
          {filtered.map((instructor) => (
            <article
              key={instructor.id}
              className="instructor-card"
              data-testid={`instructor-public-card-${instructor.id}`}
            >
              <InstructorPhoto instructor={instructor} />
              <div className="instructor-card__body">
                <h2>
                  {instructor.firstName} {instructor.lastName}
                </h2>
                {instructor.title ? <p className="instructor-card__title">{instructor.title}</p> : null}
                {instructor.dojo ? (
                  <p className="instructor-card__dojo" data-testid={`instructor-dojo-${instructor.id}`}>
                    {instructor.dojo.name} — {instructor.dojo.city}
                  </p>
                ) : null}
                {instructor.biography ? (
                  <p className="instructor-card__bio">{shortBio(instructor.biography)}</p>
                ) : null}
                <Link
                  to={`/instructeurs/${instructor.id}`}
                  className="btn-secondary btn-sm"
                  data-testid={`instructor-public-link-${instructor.id}`}
                >
                  {d.viewProfile ?? "Voir la fiche"}
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </PageSection>
  );
}
